import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../contexts/AuthContext";
import { mockApiService, shouldUseMockData } from "../mockData/mockLocketData";
import { Loader as Loader2, Settings, Shield, ClipboardList, CreditCard as Edit3, LogOut, Lock, TriangleAlert as AlertTriangle, Circle as HelpCircle, MapPin } from "lucide-react";
import BrandLogo from "../components/shared/BrandLogo";
import LanguageSwitcher from "../components/shared/LanguageSwitcher";
import { useLanguage } from "../contexts/LanguageContext";

const LandingPage = () => {
  const { locketId } = useParams();
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locket, setLocket] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [showReportDisclaimer, setShowReportDisclaimer] = useState(false);
  const [ownerPhoto, setOwnerPhoto] = useState(null);
  const [ownerName, setOwnerName] = useState("");

  useEffect(() => {
    const fetchLocket = async () => {
      setLoading(true);
      setError(null);
      try {
        let res;
        if (shouldUseMockData()) {
          try {
            res = await mockApiService.getLocketStatus(locketId);
          } catch (mockErr) {
            if (mockErr.response) throw mockErr;
            const error = new Error(mockErr.message || "QR sticker not found or server error.");
            error.response = { status: 500, data: { error: error.message } };
            throw error;
          }
        } else {
          res = await api.get(`/qr/${locketId}`);
        }
        setLocket(res.data);
        if (token) {
          try {
            if (shouldUseMockData()) {
              await mockApiService.getCustomerProfile(locketId, token);
            } else {
              await api.get(`/qr/${locketId}/profile`, {
                headers: { Authorization: `Bearer ${token}` },
              });
            }
            setIsOwner(true);
          } catch (ownerErr) {
            setIsOwner(false);
          }
        }
      } catch (err) {
        const errorMessage = err.response?.data?.error || err.data?.error || "QR sticker not found or server error.";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    if (locketId) fetchLocket();
  }, [locketId, token]);

  useEffect(() => {
    if (!locketId || !locket?.isActivated) return;
    const fetchPublicInfo = async () => {
      try {
        if (shouldUseMockData()) {
          const res = await mockApiService.getPublicEmergencyInfo(locketId);
          setOwnerName(res?.data?.customer?.name || "");
          setOwnerPhoto(res?.data?.customer?.photo || null);
        } else {
          const res = await api.get(`/emergency/${locketId}/public`);
          setOwnerName(res?.data?.customer?.name || "");
          setOwnerPhoto(res?.data?.customer?.photo || null);
        }
      } catch (_) {}
    };
    fetchPublicInfo();
  }, [locketId, locket?.isActivated]);

  if (loading)
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6 text-center max-w-sm w-full">
          <Loader2 size={36} className="text-red-600 mx-auto mb-3 animate-spin" />
          <div className="text-sm font-semibold text-slate-700">Loading QR sticker info...</div>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md border-2 border-red-300 p-6 text-center max-w-sm w-full">
          <AlertTriangle size={36} className="text-red-600 mx-auto mb-3" />
          <p className="text-base font-bold text-red-700">{error}</p>
        </div>
      </div>
    );

  if (!locket) return null;

  // Needs setup
  if (locket.needsSetup) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md border border-slate-200 p-5 text-center max-w-sm w-full">
          <Settings size={40} className="text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-extrabold text-slate-900 mb-1">Setup Needed</h1>
          <p className="text-sm text-slate-600 mb-4">
            This QR hasn't been activated. Set it up now — takes 2 minutes.
          </p>
          <button
            onClick={() => navigate(`/${locketId}/setup`)}
            className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold text-sm rounded-lg py-3 shadow-md transition-colors duration-150"
          >
            <Shield size={18} />
            Activate Now
          </button>
        </div>
      </div>
    );
  }

  // Activated
  if (locket.isActivated) {
    return (
      <div className="min-h-screen bg-slate-50 pb-16">
        {/* Sticky header */}
        <div className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-lg mx-auto px-3 py-2 flex items-center justify-between">
            <BrandLogo logoClassName="h-7" />
            <div className="flex gap-2">
              {isOwner && (
                <button
                  onClick={() => { logout(); setIsOwner(false); }}
                  className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors duration-150"
                >
                  <LogOut size={16} className="text-slate-700" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-3 pt-3 pb-4">
          {/* 1. Report Emergency — TOP */}
          <button
            onClick={() => setShowReportDisclaimer(true)}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 text-white p-3 min-h-[56px] shadow-md transition-colors duration-150 mb-3"
          >
            <AlertTriangle size={20} className="shrink-0" />
            <span className="text-base font-bold">{t("public.reportEmergency")}</span>
          </button>

          {/* 2. Status header — inline */}
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center shrink-0">
              <Shield size={16} className="text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base font-extrabold text-slate-900 leading-tight">
                {t("landing.activeTitle")}
              </h1>
              {ownerName && <p className="text-sm text-slate-600 font-semibold truncate">{ownerName}</p>}
            </div>
          </div>

          {/* 3. Owner photo — prominent identity verification card */}
          {(ownerPhoto || ownerName) && (
            <div className="mb-3 bg-white rounded-lg border-2 border-amber-300 shadow-sm overflow-hidden">
              <div className="bg-amber-50 px-3 py-2 border-b border-amber-200 flex items-center gap-2">
                <span className="text-amber-600 text-base">⚠️</span>
                <p className="text-sm font-bold text-amber-800">{t("landing.matchHolder")}</p>
              </div>
              <div className="flex flex-col items-center py-5 px-4 gap-3">
                {ownerPhoto ? (
                  <img src={ownerPhoto} alt={ownerName || "Owner"} className="w-44 h-44 rounded-lg object-cover border-2 border-slate-200 shadow-sm" />
                ) : (
                  <div className="w-44 h-44 rounded-lg bg-slate-100 border-2 border-slate-200 flex items-center justify-center">
                    <span className="text-6xl font-bold text-slate-400">
                      {(ownerName || "?").charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                {ownerName && <p className="text-lg font-extrabold text-slate-900 text-center">{ownerName}</p>}
              </div>
              <div className="bg-amber-50 border-t border-amber-200 px-3 py-2">
                <p className="text-xs text-amber-700 text-center font-medium">{t("landing.matchHolderHint")}</p>
              </div>
            </div>
          )}

          {/* 4. Options: View Info + Owner Login/Edit */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              onClick={() => navigate(`/${locketId}/public`)}
              className="flex flex-col items-center justify-center gap-1 rounded-lg bg-white border-2 border-slate-200 hover:border-red-300 hover:bg-red-50/50 p-3 min-h-[56px] shadow-sm transition-colors duration-150"
            >
              <ClipboardList size={20} className="text-red-600 shrink-0" />
              <span className="text-xs font-bold text-slate-900 leading-tight text-center">{t("landing.viewInfo")}</span>
            </button>
            {isOwner ? (
              <button
                onClick={() => navigate(`/${locketId}/info`)}
                className="flex flex-col items-center justify-center gap-1 rounded-lg bg-white border-2 border-slate-200 hover:border-red-300 hover:bg-red-50/50 p-3 min-h-[56px] shadow-sm transition-colors duration-150"
              >
                <Edit3 size={20} className="text-red-600 shrink-0" />
                <span className="text-xs font-bold text-slate-900 leading-tight text-center">{t("landing.editInfo")}</span>
              </button>
            ) : (
              <button
                onClick={() => navigate(`/${locketId}/edit`)}
                className="flex flex-col items-center justify-center gap-1 rounded-lg bg-white border-2 border-slate-200 hover:border-slate-400 hover:bg-slate-50 p-3 min-h-[56px] shadow-sm transition-colors duration-150"
              >
                <Lock size={20} className="text-slate-600 shrink-0" />
                <span className="text-xs font-bold text-slate-900 leading-tight text-center">{t("landing.ownerLogin")}</span>
              </button>
            )}
          </div>

          <button
            onClick={() => navigate(`/${locketId}/nearby`)}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-white border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50/50 p-3 min-h-[48px] shadow-sm transition-colors duration-150 mb-3"
          >
            <MapPin size={18} className="text-blue-600 shrink-0" />
            <span className="text-sm font-bold text-slate-900">Nearby Hospitals &amp; Services</span>
          </button>

          <div className="bg-white rounded-lg border border-slate-200 p-2 text-center">
            <p className="text-xs font-medium text-slate-500">
              EmergencyQR.in | DPDPA 2023 | 112 India
            </p>
          </div>
        </div>

        {/* Language floating button */}
        <div className="fixed bottom-4 right-4 z-50 flex flex-col items-center gap-1">
          <LanguageSwitcher />
        </div>

        {/* Report Emergency disclaimer modal */}
        {showReportDisclaimer && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowReportDisclaimer(false)}>
            <div className="bg-white rounded-lg shadow-md max-w-sm w-full p-4 border border-slate-200" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={22} className="text-amber-500 shrink-0" />
                <h3 className="text-base font-bold text-slate-900">{t("public.reportEmergency")}</h3>
              </div>
              <p className="text-sm text-slate-600 mb-2">
                {t("public.reportDisclaimerIntro")}
              </p>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2 mb-3">
                <p className="text-sm text-emerald-800 font-medium mb-0.5">{t("public.govRecognitionTitle")}</p>
                <p className="text-xs text-emerald-700">{t("public.govRecognitionDesc")}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowReportDisclaimer(false)}
                  className="flex-1 py-2.5 rounded-lg border-2 border-slate-300 text-slate-700 font-semibold text-sm"
                >
                  {t("shared.cancel")}
                </button>
                <button
                  onClick={() => { setShowReportDisclaimer(false); navigate(`/${locketId}/report`); }}
                  className="flex-1 py-2.5 rounded-lg bg-red-600 text-white font-semibold text-sm hover:bg-red-700"
                >
                  {t("public.understandReport")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Error from backend
  if (locket.error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md border-2 border-red-300 p-6 text-center max-w-sm w-full">
          <AlertTriangle size={36} className="text-red-600 mx-auto mb-3" />
          <h1 className="text-xl font-extrabold text-red-700 mb-1">Error</h1>
          <p className="text-base font-bold text-red-600">{locket.message || locket.error}</p>
        </div>
      </div>
    );
  }

  // Unknown state
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-md border-2 border-amber-300 p-6 text-center max-w-sm w-full">
        <HelpCircle size={36} className="text-amber-500 mx-auto mb-3" />
        <h1 className="text-xl font-extrabold text-amber-700 mb-1">Something Doesn't Look Right</h1>
        <p className="text-sm font-semibold text-amber-600">
          We found this QR code but couldn't determine its status. Email contact@dityatechnologies.in for help.
        </p>
      </div>
    </div>
  );
};

export default LandingPage;
